/**
 * DesignerToolbar — Top toolbar for the Visual Workspace Designer.
 *
 * Contains: back button, workspace title, undo/redo, preview, save.
 */
import React from 'react';
import {
  Box,
  IconButton,
  Button,
  Tooltip,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import PreviewIcon from '@mui/icons-material/Visibility';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SaveIcon from '@mui/icons-material/Save';

import { useDesignerState, useDesignerHistory, useDesignerDispatch } from './DesignerStateContext';

export default function DesignerToolbar({ onClose, onSave, onPreview, onViewJson, saving, colors }) {
  const designer = useDesignerState();
  const dispatch = useDesignerDispatch();
  const { canUndo, canRedo } = useDesignerHistory();
  const { workspace, isDirty } = designer;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: 48,
        px: 1.5,
        borderBottom: `1px solid ${colors?.border || '#333'}`,
        bgcolor: colors?.surface || '#1a1a2e',
        gap: 1,
        flexShrink: 0,
      }}
    >
      {/* Back button */}
      <Tooltip title="Back to Workspaces">
        <IconButton size="small" onClick={onClose} sx={{ color: colors?.textSecondary || '#999' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Workspace title */}
      <Typography
        variant="subtitle2"
        sx={{ color: colors?.text || '#e0e0e0', fontWeight: 600, mr: 1 }}
      >
        {workspace.title || 'Untitled Workspace'}
      </Typography>

      {workspace.code && (
        <Chip
          label={workspace.code}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            bgcolor: 'rgba(255,255,255,0.08)',
            color: colors?.textSecondary || '#999',
          }}
        />
      )}

      {isDirty && (
        <Chip
          label="Unsaved"
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            bgcolor: 'rgba(255,152,0,0.15)',
            color: '#ffb74d',
          }}
        />
      )}

      <Box sx={{ flex: 1 }} />

      {/* Undo / Redo */}
      <Tooltip title="Undo (Ctrl+Z)">
        <span>
          <IconButton
            size="small"
            disabled={!canUndo}
            onClick={() => dispatch({ type: 'UNDO' })}
            sx={{ color: colors?.textSecondary || '#999' }}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Redo (Ctrl+Y)">
        <span>
          <IconButton
            size="small"
            disabled={!canRedo}
            onClick={() => dispatch({ type: 'REDO' })}
            sx={{ color: colors?.textSecondary || '#999' }}
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Box sx={{ width: 1, height: 24, bgcolor: colors?.border || '#333', mx: 0.5 }} />

      {/* Preview */}
      <Tooltip title="Preview workspace">
        <IconButton
          size="small"
          onClick={onPreview}
          sx={{ color: colors?.textSecondary || '#999' }}
        >
          <PreviewIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* View / Edit JSON */}
      <Tooltip title="View / Edit JSON">
        <IconButton
          size="small"
          onClick={onViewJson}
          sx={{ color: colors?.textSecondary || '#999' }}
        >
          <DataObjectIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Save */}
      <Button
        size="small"
        variant="contained"
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
        disabled={saving || !isDirty}
        onClick={onSave}
        sx={{
          textTransform: 'none',
          bgcolor: '#22c55e',
          '&:hover': { bgcolor: '#16a34a' },
          '&.Mui-disabled': { bgcolor: 'rgba(34,197,94,0.3)', color: 'rgba(255,255,255,0.5)' },
          fontSize: '0.8rem',
          minWidth: 80,
        }}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </Box>
  );
}
